import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { settingsService } from '../../services/settingsService';

const SupportPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    loadSupportPage();
  }, []);

  const loadSupportPage = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const setting = await settingsService?.getSettingByKey('page_support');
      
      if (setting?.settingValue) {
        setContent(setting?.settingValue);
      } else {
        // Default content if not found in database
        setContent({
          title: 'Support Center',
          description: 'Get help with RoleCall and find answers to common questions',
          contactEmail: 'support@rolecall.app',
          faqs: [
            {
              question: 'How do I get started with RoleCall?',
              answer: 'Sign up for an account, choose your subscription plan, and start exploring our comprehensive musical theatre song library. We recommend beginning with our beginner-friendly content and working your way up.'
            },
            {
              question: 'What subscription plans are available?',
              answer: 'RoleCall offers a single plan at $9.99/month with unlimited access to all content, including professional video lessons, song analyses, and learning tools.'
            },
            {
              question: 'Can I cancel my subscription at any time?',
              answer: 'Yes, you can cancel your subscription at any time from your account settings. Your access will continue until the end of your current billing period.'
            },
            {
              question: 'How do I track my learning progress?',
              answer: 'Your progress is automatically tracked as you complete lessons. View your progress dashboard to see completed lessons, earned badges, and learning streaks.'
            },
            {
              question: 'Are the videos downloadable?',
              answer: 'Video content is available for streaming within the platform but cannot be downloaded to protect our instructors\' intellectual property.'
            },
            {
              question: 'How often is new content added?',
              answer: 'We regularly add new songs, musicals, and video content. Check the "Featured" section to see our latest additions.'
            },
            {
              question: 'Can I request specific songs or musicals?',
              answer: 'Yes! We welcome content requests. Contact us through the support email and our team will consider adding requested content in future updates.'
            },
            {
              question: 'Is there a mobile app available?',
              answer: 'Yes, RoleCall is available on both iOS and Android. Download from the App Store or Google Play to learn on the go.'
            }
          ],
          sections: [
            {
              title: 'Getting Help',
              content: 'If you need assistance, you can reach out to our support team via email or browse our frequently asked questions below. We typically respond within 24 hours.'
            },
            {
              title: 'Technical Issues',
              content: 'For technical problems, please include your device type, browser version, and a description of the issue. Screenshots are helpful when reporting bugs.'
            },
            {
              title: 'Account Questions',
              content: 'For account-related inquiries including billing, subscription changes, or profile updates, please contact our support team directly.'
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading support page:', err);
      setError(err?.message || 'Failed to load support page');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading Support Center..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center p-6">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 max-w-md">
          <Icon name="AlertTriangle" size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white text-center mb-2">
            Unable to Load Support Page
          </h2>
          <p className="text-blue-100 text-center mb-6">{error}</p>
          <Button onClick={loadSupportPage} className="w-full" iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Support - RoleCall</title>
        <meta name="description" content="Get help with RoleCall - Find answers to common questions and contact our support team" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        {/* Navigation Bar */}
        <nav className="container mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo - Musical Theatre Learning Platform"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
              iconName="Home"
              iconPosition="left"
            >
              Back to Home
            </Button>
          </div>
        </nav>

        {/* Content */}
        <div className="container mx-auto px-6 py-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
                {content?.title || 'Support Center'}
              </h1>
              {content?.description && (
                <p className="text-xl text-blue-100">
                  {content?.description}
                </p>
              )}
            </div>

            {/* Contact Section */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 mb-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-gradient-to-r from-purple-600 to-pink-500 w-16 h-16 rounded-xl flex items-center justify-center">
                  <Icon name="Mail" size={32} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-white">
                    Contact Support
                  </h2>
                  <p className="text-blue-200">
                    We're here to help
                  </p>
                </div>
              </div>
              
              {content?.contactEmail && (
                <a 
                  href={`mailto:${content?.contactEmail}`}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl hover:opacity-90 transition-opacity"
                >
                  <Icon name="Mail" size={20} className="mr-2" />
                  {content?.contactEmail}
                </a>
              )}
            </div>

            {/* General Information Sections */}
            {content?.sections?.map((section, index) => (
              <div key={index} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 mb-8">
                <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                  {section?.title}
                </h2>
                <p className="text-blue-100 leading-relaxed whitespace-pre-wrap">
                  {section?.content}
                </p>
              </div>
            ))}

            {/* FAQ Section */}
            {content?.faqs && content?.faqs?.length > 0 && (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8">
                <h2 className="text-2xl font-heading font-semibold text-white mb-6">
                  Frequently Asked Questions
                </h2>
                
                <div className="space-y-4">
                  {content?.faqs?.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white bg-opacity-5 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white hover:bg-opacity-5 transition-colors"
                      >
                        <span className="font-heading font-semibold text-white text-lg pr-4">
                          {faq?.question}
                        </span>
                        <Icon
                          name="ChevronDown"
                          size={24}
                          className={`text-white flex-shrink-0 transition-transform ${
                            activeFaq === index ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {activeFaq === index && (
                        <div className="px-6 pb-4">
                          <p className="text-blue-100 leading-relaxed">
                            {faq?.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="py-8 bg-black bg-opacity-30 mt-12">
          <div className="container mx-auto px-6 text-center">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo"
              className="h-8 w-auto mx-auto mb-4"
            />
            <p className="text-blue-200">
              © {new Date()?.getFullYear()} RoleCall. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SupportPage;